import { AtRiskTringleIcon, CriticalCircleIcon } from '@/assets/icons/icons';

export enum ShipmentAlertType {
  WARNING = 1, // yellow triangle
  CRITICAL = 0, // red exclamation
}

export enum ShipmentProgress {
  DELIVERED = '3',
  PENDING = '4',
  ARCHIVED = '5',
  IN_TRANSIT = '6',
  AWAITING_PICKUP = '7',
  DATA_LOADING = '8',
  DELIVERY_EXCEPTION = '9',
  NOT_FOUND = '10',
  NO_CARRIER_DATA = '11',
  TRACKING_ID_ISSUE = '12',
}

export const SHIPMENT_PROGRESS_COLORS: Record<ShipmentProgress, string> = {
  [ShipmentProgress.DELIVERED]: '#0CBDBA', // green
  [ShipmentProgress.PENDING]: '#FFC043', // yellow
  [ShipmentProgress.ARCHIVED]: '#5E5E5E', // gray
  [ShipmentProgress.IN_TRANSIT]: '#266EF1', // blue
  [ShipmentProgress.AWAITING_PICKUP]: '#8F8F8F', // dark gray
  [ShipmentProgress.DATA_LOADING]: '#6B7280',
  [ShipmentProgress.DELIVERY_EXCEPTION]: '#DE1135', // red
  [ShipmentProgress.NOT_FOUND]: '#FF6600',
  [ShipmentProgress.NO_CARRIER_DATA]: '#7C3AED', // purple
  [ShipmentProgress.TRACKING_ID_ISSUE]: '#B91C1C',
};

/** Canonical progress colors for companies with Customer/Execution Leg views. */
export const CUSTOMER_LEG_CALENDAR_STATUS_COLORS: Record<string, string> = {
  INCOMPLETE: '#FF6600',
  PENDING: '#FFC043',
  RATED: '#8F8F8F',
  BOOKED: '#7C3AED',
  IN_TRANSIT: '#266EF1',
  DELIVERED: '#0CBDBA',
  CANCELLED: '#5E5E5E',
};

const normalizeCustomerLegStatus = (status?: string | null): string =>
  String(status ?? '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');

/**
 * Calendar responses include both a canonical status and the legacy shipment
 * type. Customer/Execution Leg calendars must use the canonical status: the
 * legacy numeric type is not reliable for an Execution Leg and can make every
 * event appear Pending or fall back to blue.
 */
export const getCalendarShipmentBorderColor = (
  status: string | null | undefined,
  shipmentType: string | null | undefined,
  enableCustomerLeg: boolean
): string => {
  if (enableCustomerLeg) {
    const canonicalColor =
      CUSTOMER_LEG_CALENDAR_STATUS_COLORS[normalizeCustomerLegStatus(status)];
    if (canonicalColor) return canonicalColor;
  }

  const key = shipmentType as ShipmentProgress;
  return SHIPMENT_PROGRESS_COLORS[key] ?? '#2563EB';
};

export const AlertIcon = ({ type }: { type: ShipmentAlertType }) => {
  if (type === ShipmentAlertType.CRITICAL) {
    return <CriticalCircleIcon />;
  }

  if (type === ShipmentAlertType.WARNING) {
    return <AtRiskTringleIcon />;
  }

  return null;
};

type TeamDotsProps = {
  colors: string[];
};

export const TeamDots = ({ colors }: TeamDotsProps) => {
  const visible = colors.slice(0, 3);
  const remaining = colors.length - visible.length;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      {visible.map((color, idx) => (
        <span
          key={idx}
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: color,
          }}
        />
      ))}

      {remaining > 0 && (
        <span
          style={{
            fontSize: 11,
            color: '#6B7280',
            marginLeft: 2,
          }}
        >
          +{remaining}
        </span>
      )}
    </div>
  );
};
